import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, Loader2, Shield } from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PasswordResetModal = ({ isOpen, onPasswordChanged }) => {
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const validatePassword = (password) => {
    const errs = [];
    if (password.length < 8) errs.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errs.push('At least 1 uppercase letter');
    if (!/\d/.test(password)) errs.push('At least 1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errs.push('At least 1 special character');
    return errs;
  };

  const requirements = [
    'At least 8 characters',
    'At least 1 uppercase letter', 
    'At least 1 number',
    'At least 1 special character'
  ];

  const handleSubmit = async () => {
    const validationErrors = validatePassword(passwordForm.new_password);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.post(`${API_BASE}/api/profile/change-password`, {
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password updated successfully!');
      onPasswordChanged();
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <DialogTitle>Create Your Password</DialogTitle>
              <DialogDescription>
                For security, please create a new password for your account.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPasswords.new ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, new_password: e.target.value });
                  setErrors(validatePassword(e.target.value));
                }}
                placeholder="Enter your new password"
                data-testid="reset-new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showPasswords.confirm ? "text" : "password"}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirm your new password"
                data-testid="reset-confirm-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs font-medium text-muted-foreground mb-2">Password Requirements:</p>
            <div className="grid grid-cols-2 gap-1">
              {requirements.map((req) => {
                const met = !errors.includes(req) && passwordForm.new_password.length > 0;
                return (
                  <div
                    key={req}
                    className={cn(
                      "flex items-center gap-1.5 text-xs",
                      met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center",
                      met ? "bg-green-100 dark:bg-green-900" : "bg-muted-foreground/20"
                    )}>
                      {met && <Check className="w-3 h-3" />}
                    </div>
                    {req}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleSubmit}
            disabled={saving || errors.length > 0 || !passwordForm.new_password || !passwordForm.confirm_password}
            className="w-full"
            data-testid="reset-password-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Set Password & Continue'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordResetModal;
